import { specFractionDigits, specValue } from '../cars/carFigures';
import {
    formatSpecNumber,
    specFigure,
    specUnitKey,
} from '../cars/carProperties';
import { carName } from '../cars/carCatalogue';
import { duelMargin, duelShare } from './duelSpecs';

/**
 * What the reveal says, composed from figures the catalogue already carries
 * (SPEC-007 PR-7.4).
 *
 * The product promise is "an informative explanation of the answer", and the
 * decision underneath this module is that NONE OF IT IS AUTHORED. A note per car
 * per question would be twenty-four cars times seven questions of prose nobody has
 * commissioned, and it would go stale the first time a figure was corrected in
 * `carSpecs.js`. So the explanation is derived: the margin and the two shares of
 * one bar. THERE IS NO PROSE IN HERE AT ALL — this module answers figures, and
 * every one of them is read off the same catalogue the spec table reads.
 *
 * IT ANSWERS THE COMPARISON AND NOTHING BESIDE IT. Two supporting figures used
 * to come with it — a car that won on 0-100 was shown its power-to-weight — and
 * they went the way the fun fact and the per-question sentence went before them
 * (D-70): the screen that draws this now has the answer where the question was,
 * and a row of small type under the two cards is a fourth thing to read in a
 * beat between two taps. `SUPPORTING_FIGURES` went with them rather than being
 * left as a table nothing indexes.
 *
 * There is deliberately NO per-question sentence here, and there was one. With
 * the two figures and the gap drawn on a shared gauge, "the 512 TR runs to 314
 * km/h, 44 km/h clear of the 993" is the same fact a third time (SPEC-007 D-23).
 *
 * The winner's FUN FACT was the other thing this module answered, and it went
 * the same way for the same reason (SPEC-007 D-59): a paragraph of prose under a
 * gauge, a row of figures and two boards is a fourth thing to read in a band
 * that is already three, and the reveal is a beat between two taps rather than a
 * page. It is still on the car's own screen, whole, where somebody who wants to
 * read about the car is already looking.
 *
 * Like every other domain module here it returns NUMBERS AND MESSAGE KEYS, never
 * resolved copy.
 */

/**
 * The two cars as the two pieces of ONE BAR, in the order they were presented.
 *
 * The gauge is the reveal's centrepiece and this is the arithmetic behind it:
 * a single horizontal track split between the two figures, each car taking its
 * share of their total. The shares add up to one, so the split point is the
 * comparison — and how far that point sits from the middle is the difference,
 * without the reader subtracting anything.
 *
 * The larger share is not always the winner's, and that is correct rather than a
 * bug to invert away: on "which one is lighter" the heavier car takes more of
 * the bar and it lost. LENGTH IS THE QUANTITY AND COLOUR IS THE ANSWER. That
 * separation is what lets one drawing serve all seven questions, including
 * "which one is rarer", where the winning car's share is a sliver and the sliver
 * is exactly the point.
 *
 * Presented order rather than winner-first, because the bar sits under the two
 * cards and its two ends line up with them — a winner-first split would put the
 * left card's figure on the right-hand end half the time.
 */
function comparison(verdict, { languageCode }) {
    const cars = verdict.duel.cars;
    const values = cars.map((car) => specValue(car, verdict.spec) ?? 0);
    const total = values[0] + values[1];

    return cars.map((car, index) => {
        const figure = specFigure(car, verdict.spec, { languageCode });

        return {
            carId: car.id,
            name: carName(car),
            // Carried per entry rather than once on the insight, because an end
            // of the bar is handed its own entry and nothing else.
            labelKey: figure.labelKey,
            value: figure.value,
            unitKey: figure.unitKey,
            share: duelShare(values[index], total),
            isWinner: car.id === verdict.winner.id,
        };
    });
}

/**
 * Everything the reveal draws, as data.
 *
 * @param {object} verdict - what `judgeDuel` returned.
 * @param {object} [options] - `languageCode`, for grouping the figures.
 * @returns {object|null} null for a verdict with no two cars in it, which is a
 *   caller bug rather than a state — the screen renders no reveal without one.
 */
export function duelInsight(verdict, { languageCode = 'en' } = {}) {
    if (!verdict?.winner || !verdict?.loser) {
        return null;
    }

    const { spec } = verdict;
    const winnerFigure = specFigure(verdict.winner, spec, { languageCode });

    return {
        spec,
        comparison: comparison(verdict, { languageCode }),
        margin: formatSpecNumber(duelMargin(verdict.winner, verdict.loser, spec), {
            languageCode,
            fractionDigits: specFractionDigits(spec),
        }),
        unitKey: specUnitKey(spec),
        // `production` is the one question whose figure has no unit, and the
        // screen has to know before it resolves a key that would print itself.
        hasUnit: Boolean(winnerFigure.unitKey),
    };
}
